/**
 * EntryFinaleLayer — SIGNUP-1: the overlay `playSignInFinale` drives.
 * Always mounted (everything starts invisible) so the finale can begin
 * on the same frame the sign-in succeeds, with no state round-trip.
 *
 * ENTRY-80s — the parts, bottom to top (see entryMotion.ts for the
 * beats they play):
 *
 *   [data-grid] + [data-grid-lines]   the perspective floor and the
 *                                     line layer that rushes down it
 *   [data-horizon]                    the glow where floor meets sky
 *   [data-stars]  ×2                  the warp starfield, two passes
 *   [data-streak] ×26                 colour light trails: soft, colour,
 *                                     and a hot core in one rod
 *   [data-shock]                      the white impact ring
 *   [data-ring]   ×6                  a rainbow ring per colour
 *   [data-orb] › [data-orb-colour] ×6 the sun — a disc + halo per colour
 *                                     under a white core
 *   [data-core]                       the rainbow annulus
 *   [data-logo-wrap] › glow ×2, [data-logo-glow] › [data-logo-c] /
 *                   [data-logo-m] / [data-logo-main] › [data-sweep]
 *                                     the wordmark: cyan and magenta
 *                                     copies under a white one, all cut
 *                                     from the same PNG with mask-image,
 *                                     two blurred copies behind for the
 *                                     glow, and the light that sweeps it
 *   [data-scan]                       scanlines and a vignette
 *   [data-flash]                      the bloom to white
 *
 * Everything centred sits at `top: min(50%, 450px)`, the same centre
 * the tiles converge on (entryMotion caps it at a 900px-tall stage).
 *
 * ENTRY-SMOOTH (Sep 29) — every part here moves by transform and
 * opacity only, and every glow is painted once and never changes: no
 * blend modes over the whole screen, no shapes animated inside an SVG,
 * no animated shadows or filters. See entryMotion.ts for why.
 */
import { useMemo, type CSSProperties } from 'react';
import orbitLogoWhite from '@/assets/orbit-retro-logo-white.png';
import { ENTRY_PULSE, NEON, makeStars, makeStreaks } from './entryMotion';

const MID = 'min(50%, 450px)';
const RING = 'clamp(160px, 20vw, 280px)';

const LOGO_MASK: CSSProperties = {
  WebkitMaskImage: `url(${orbitLogoWhite})`,
  maskImage: `url(${orbitLogoWhite})`,
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
};

const GRID_MASK = 'linear-gradient(to bottom, transparent 0%, #000 30%, #000 100%)';
const CORE_MASK = 'radial-gradient(circle, transparent 0 34%, #000 50%, #000 70%, transparent 74%)';

export function EntryFinaleLayer() {
  const streaks = useMemo(() => makeStreaks(), []);
  const stars = useMemo(() => makeStars(), []);
  return (
    <div data-finale="1" className="pointer-events-none absolute inset-0 z-[60] overflow-hidden" aria-hidden>
      {/* The floor. A wide plane hinged at the horizon and tipped
          toward the camera; the vertical lines converge on the
          vanishing point by themselves, the horizontal ones ride the
          inner layer down it. Each line is drawn twice — a 2px core
          and a soft 9px halo — which is glow for the price of a
          gradient. */}
      <div
        data-grid="1"
        className="absolute left-1/2 h-[72vh] w-[320vw] opacity-0"
        style={{
          top: MID,
          transform: 'translateX(-50%) perspective(520px) rotateX(64deg)',
          transformOrigin: '50% 0',
          WebkitMaskImage: GRID_MASK,
          maskImage: GRID_MASK,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              `repeating-linear-gradient(to right, ${NEON.blue} 0 2px, transparent 2px 120px),` +
              `repeating-linear-gradient(to right, rgba(47,107,232,.28) 0 9px, transparent 9px 120px)`,
            backgroundPosition: '0 0, -3.5px 0',
          }}
        />
        <div
          data-grid-lines="1"
          className="absolute left-0 right-0"
          style={{
            top: -64,
            bottom: -64,
            backgroundImage:
              `repeating-linear-gradient(to bottom, ${NEON.violet} 0 2px, transparent 2px 64px),` +
              `repeating-linear-gradient(to bottom, rgba(180,91,200,.32) 0 9px, transparent 9px 64px)`,
            backgroundPosition: '0 0, 0 -3.5px',
          }}
        />
      </div>
      <div
        data-horizon="1"
        className="absolute left-1/2 h-[28vh] w-[150vw] opacity-0"
        style={{
          top: MID,
          transform: 'translate(-50%,-50%)',
          background:
            'radial-gradient(50% 50% at 50% 50%, rgba(180,91,200,.5) 0%, rgba(47,107,232,.22) 42%, transparent 70%)',
        }}
      />

      {/* The warp starfield — scaled uniformly from the centre
          (`slice`), so the rush is radial on every screen shape. Each
          pass is its own layer, so the scale runs on the compositor. */}
      {[0, 1].map((pass) => (
        <div key={pass} data-stars="1" className="absolute inset-0 opacity-0" style={{ willChange: 'transform, opacity' }}>
          <svg viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice" className="h-full w-full" fill="none">
            {stars.map((s, i) => (
              <line
                key={i}
                x1={s.x1}
                y1={s.y1}
                x2={s.x2}
                y2={s.y2}
                stroke="#FFFFFF"
                strokeWidth={s.w}
                strokeLinecap="round"
                opacity={pass === 0 ? s.o : s.o * 0.6}
              />
            ))}
          </svg>
        </div>
      ))}

      {/* Light trails. Points are in a 1000×1000 space stretched to the
          stage, so a trail's end at (500,500) is the exact centre the
          rods and the rod-from-the-button converge on. Each trail is one
          rod — soft glow, colour line, white-hot core — whose length,
          angle and place entryMotion sets from the stage's size when the
          finale starts. */}
      {streaks.map((l, i) => (
        <div
          key={i}
          data-streak="1"
          data-x={l.x}
          data-y={l.y}
          data-x2={l.x2}
          data-y2={l.y2}
          className="absolute left-0 top-0 w-0 opacity-0"
          style={{
            height: l.w * 4,
            borderRadius: l.w * 2,
            transformOrigin: '0 50%',
            background: `linear-gradient(to bottom, ${l.c}4D 0 37.5%, ${l.c} 37.5% 45.6%, #FFFFFFD9 45.6% 54.4%, ${l.c} 54.4% 62.5%, ${l.c}4D 62.5% 100%)`,
          }}
        />
      ))}

      {/* Impact ring, then one ring per colour. */}
      <div
        data-shock="1"
        className="absolute left-1/2 aspect-square rounded-full opacity-0"
        style={{
          top: MID,
          willChange: 'transform, opacity',
          width: RING,
          border: '3px solid #FFFFFF',
          boxShadow: '0 0 26px #FFFFFF, inset 0 0 26px #FFFFFF',
        }}
      />
      {ENTRY_PULSE.map((c, i) => (
        <div
          key={i}
          data-ring="1"
          className="absolute left-1/2 aspect-square rounded-full opacity-0"
          style={{
            top: MID,
            willChange: 'transform, opacity',
            width: RING,
            border: `3px solid ${c}`,
            boxShadow: `0 0 22px ${c}, inset 0 0 22px ${c}`,
          }}
        />
      ))}

      {/* The sun: a colour disc and halo per colour — each painted
          once; the colour cycle fades the next one in over the last
          (not a filter, whose clip box would square off the glow) —
          with a white-hot core on top that never changes. */}
      <div
        data-orb="1"
        className="absolute left-1/2 aspect-square rounded-full opacity-0"
        style={{ top: MID, width: 'clamp(150px, 18vw, 260px)', willChange: 'transform, opacity' }}
      >
        {ENTRY_PULSE.map((c, i) => (
          <div
            key={c}
            data-orb-colour={i}
            className="absolute rounded-full"
            style={{
              // The disc, its bright inner rim and its halo in one gradient
              // (the old 60px + 170px glows and the inset white rim) — a
              // gradient is cheap to draw; a 170px blur is not. The disc's
              // edge sits 190px in from this layer's edge.
              inset: -190,
              opacity: i === 0 ? 1 : 0,
              background: `radial-gradient(circle closest-side, ${c} 0, ${c} calc(100% - 232px), color-mix(in srgb, ${c} 62%, #fff) calc(100% - 191px), ${c} calc(100% - 189px), ${c}F2 calc(100% - 178px), ${c}99 calc(100% - 150px), ${c}4D calc(100% - 100px), ${c}1A calc(100% - 40px), ${c}00 100%)`,
            }}
          />
        ))}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'radial-gradient(circle, #FFFFFF 0%, rgba(255,255,255,.96) 16%, rgba(255,255,255,.4) 38%, rgba(255,255,255,0) 66%)',
          }}
        />
      </div>

      {/* The rainbow annulus — the old spinning core, hollowed so the
          wordmark can sit inside it; an orbit ring, in every colour. */}
      <div
        data-core="1"
        className="absolute left-1/2 aspect-square rounded-full opacity-0"
        style={{
          top: MID,
          width: 'clamp(300px, 44vw, 620px)',
          // Drawn once at this size and scaled on the GPU as it rushes past
          // (×11) — it's a blur, so the scale-up can't be seen, but
          // re-drawing it at ×11 would be.
          willChange: 'transform, opacity',
        }}
      >
        {/* The blur and the mask live on this inner layer, which never
            moves, so they're drawn into the ring's picture once. On the
            moving element itself, the blur was re-run on the whole ring
            — at ×11 — every frame. */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'conic-gradient(from 0deg,#2F6BE8,#7FB2E8,#3FC4B4,#B5D334,#E8B54A,#F08A6E,#E86A7A,#B45BC8,#6A5AD0,#2F6BE8)',
            filter: 'blur(14px)',
            WebkitMaskImage: CORE_MASK,
            maskImage: CORE_MASK,
          }}
        />
      </div>

      {/* The wordmark. Its glow is two blurred copies of the mark behind
          it, drawn once — a drop-shadow over the copies re-rendered
          every frame they moved. The blur sits on a wrapper OUTSIDE each
          masked copy: a filter on a masked element would be cut by its
          own mask. (blur(8px) / blur(24px) match the old 16px / 48px
          drop-shadows, whose radius is twice the blur.) */}
      <div
        data-logo-wrap="1"
        className="absolute left-1/2 opacity-0"
        style={{ top: MID, width: 'clamp(230px, 44vw, 600px)', aspectRatio: '1920 / 1358', willChange: 'transform, opacity' }}
      >
        {/* Each glow is drawn at half size (half the blur) and scaled up
            2× — a quarter of the pixels to blur, and a glow has no
            detail to lose. */}
        <div className="absolute left-1/4 top-1/4 h-1/2 w-1/2" style={{ filter: 'blur(12px)', opacity: 0.75, transform: 'scale(2)' }}>
          <div className="absolute inset-0" style={{ ...LOGO_MASK, background: 'rgb(180,91,200)' }} />
        </div>
        <div className="absolute left-1/4 top-1/4 h-1/2 w-1/2" style={{ filter: 'blur(4px)', opacity: 0.95, transform: 'scale(2)' }}>
          <div className="absolute inset-0" style={{ ...LOGO_MASK, background: 'rgb(47,107,232)' }} />
        </div>
        <div data-logo-glow="1" className="absolute inset-0">
          <div data-logo-c="1" className="absolute inset-0 opacity-0" style={{ ...LOGO_MASK, background: NEON.teal, mixBlendMode: 'screen' }} />
          <div data-logo-m="1" className="absolute inset-0 opacity-0" style={{ ...LOGO_MASK, background: NEON.pink, mixBlendMode: 'screen' }} />
          <div data-logo-main="1" className="absolute inset-0 overflow-hidden" style={{ ...LOGO_MASK, background: '#DCE5F8' }}>
            <div
              data-sweep="1"
              className="absolute inset-y-0 left-0 w-full"
              style={{
                transform: 'translateX(-120%)',
                background: 'linear-gradient(105deg, transparent 32%, #FFFFFF 50%, transparent 68%)',
              }}
            />
          </div>
        </div>
      </div>

      {/* A CRT is watching: scanlines and a vignette, faint. */}
      <div
        data-scan="1"
        className="absolute inset-0 opacity-0"
        style={{
          background:
            'repeating-linear-gradient(to bottom, rgba(0,0,0,.2) 0 1px, transparent 1px 3px),' +
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,.55) 100%)',
        }}
      />

      <div
        data-flash="1"
        className="absolute inset-0 opacity-0"
        style={{
          background:
            'radial-gradient(circle at 50% 50%,#FFFFFF 0%,rgba(240,245,255,.98) 30%,rgba(215,228,255,.92) 62%,rgba(190,210,255,.85) 100%)',
        }}
      />
    </div>
  );
}

export default EntryFinaleLayer;
